import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import {
  Sparkles, Plus, ChevronLeft, ChevronRight, MessageSquarePlus,
  Pencil, Trash2, AlertTriangle, X,
} from "lucide-react";
import { useAuth, formatApiError } from "../contexts/AuthContext";
import { UpdateFormModal } from "../components/UpdateFormModal";
import { SuggestIdeaModal } from "../components/SuggestIdeaModal";

const API = process.env.REACT_APP_BACKEND_URL;
const PAGE_SIZE = 8;

const STATUS_LABELS = {
  idea: { label: "Idée", emoji: "💡", className: "cbm-update-badge-idea" },
  planned: { label: "À venir", emoji: "📌", className: "cbm-update-badge-planned" },
  in_progress: { label: "En cours", emoji: "🚧", className: "cbm-update-badge-progress" },
  live: { label: "Live", emoji: "✅", className: "cbm-update-badge-live" },
  bugfix: { label: "Bug corrigé", emoji: "🐛", className: "cbm-update-badge-bugfix" },
};

function formatDate(iso) {
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleDateString("fr-FR", { day: "2-digit", month: "long", year: "numeric" });
  } catch {
    return null;
  }
}

function StatusBadge({ status }) {
  const meta = STATUS_LABELS[status] || { label: status, emoji: "•", className: "cbm-update-badge-planned" };
  return (
    <span
      data-testid={`status-badge-${status}`}
      className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold ${meta.className}`}
    >
      <span aria-hidden>{meta.emoji}</span>
      {meta.label}
    </span>
  );
}

function ConfirmDeleteDialog({ open, item, onConfirm, onCancel, loading }) {
  if (!open) return null;
  return (
    <div
      className="fixed inset-0 z-[60] bg-black/60 flex items-center justify-center p-4 animate-fade-in"
      onClick={onCancel}
      data-testid="confirm-delete-dialog"
    >
      <div
        className="cbm-card w-full max-w-sm"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between p-5 border-b cbm-divider">
          <div className="flex items-center gap-2.5">
            <div
              className="w-9 h-9 rounded-lg flex items-center justify-center"
              style={{ background: "rgba(220,38,38,0.15)", border: "1px solid rgba(220,38,38,0.3)" }}
            >
              <AlertTriangle size={16} className="text-red-400" />
            </div>
            <div>
              <h2 className="font-outfit font-semibold text-white text-base leading-tight">Supprimer cette nouveauté ?</h2>
              <p className="text-cbm-muted text-xs mt-0.5">Cette action est irréversible.</p>
            </div>
          </div>
          <button
            data-testid="close-confirm-delete"
            onClick={onCancel}
            className="text-cbm-muted hover:text-white p-1 transition-colors"
            aria-label="Fermer"
          >
            <X size={18} />
          </button>
        </div>
        <div className="p-5 space-y-4">
          <p className="text-sm leading-relaxed" style={{ color: "#CFD9EC" }}>
            Vous êtes sur le point de supprimer <span className="font-semibold text-white">« {item?.title} »</span>.
          </p>
          <div className="flex gap-2">
            <button
              data-testid="cancel-delete"
              onClick={onCancel}
              className="cbm-btn-secondary text-sm flex-1 justify-center py-2.5"
            >
              Annuler
            </button>
            <button
              data-testid="confirm-delete"
              onClick={onConfirm}
              disabled={loading}
              className="text-sm flex-1 justify-center py-2.5 rounded-lg font-semibold transition-all disabled:opacity-60 inline-flex items-center gap-1.5"
              style={{
                background: "rgba(220,38,38,0.18)",
                color: "#FCA5A5",
                border: "1px solid rgba(220,38,38,0.5)",
              }}
            >
              <Trash2 size={14} /> {loading ? "Suppression…" : "Supprimer"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function Nouveautes() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(1);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [showSuggest, setShowSuggest] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [actionError, setActionError] = useState("");

  const isAdmin = user?.role === "admin";

  const load = useCallback(async (p = 1) => {
    setLoading(true);
    try {
      const { data } = await axios.get(
        `${API}/api/updates?page=${p}&limit=${PAGE_SIZE}`,
        { withCredentials: true }
      );
      setItems(data.items || []);
      setTotal(data.total || 0);
      setPages(data.pages || 1);
      setPage(data.page || p);
    } catch {
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load(1);
    axios.post(`${API}/api/updates/seen`, {}, { withCredentials: true }).catch(() => {});
  }, [load]);

  const goPrev = () => { if (page > 1) load(page - 1); };
  const goNext = () => { if (page < pages) load(page + 1); };

  const openCreate = () => {
    setEditing(null);
    setShowForm(true);
  };

  const openEdit = (item) => {
    setEditing(item);
    setShowForm(true);
  };

  const onSaved = () => {
    // Reload current page; if a new item was created, it lands on page 1 sorted desc
    load(editing ? page : 1);
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setActionError("");
    setDeleting(true);
    try {
      await axios.delete(`${API}/api/updates/${deleteTarget._id}`, { withCredentials: true });
      setDeleteTarget(null);
      // If we deleted the last item of the current page, go back one page
      const nextPage = items.length === 1 && page > 1 ? page - 1 : page;
      load(nextPage);
    } catch (err) {
      setActionError(formatApiError(err.response?.data?.detail));
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="px-4 lg:px-8 py-8 lg:py-10">
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <div className="flex items-start justify-between gap-4 mb-8">
          <div>
            <div className="flex items-center gap-2 mb-1.5">
              <Sparkles size={20} className="text-gold" />
              <h1 className="font-outfit font-bold text-white text-2xl lg:text-3xl">Nouveautés</h1>
            </div>
            <p className="text-cbm-muted text-sm">
              Suivez l'évolution du club et les améliorations à venir.
            </p>
          </div>
          {isAdmin && (
            <button
              data-testid="add-update-button"
              onClick={openCreate}
              className="cbm-btn-primary text-sm flex-shrink-0 px-3.5 py-2"
            >
              <Plus size={14} /> Ajouter une nouveauté
            </button>
          )}
        </div>

        {actionError && (
          <div
            data-testid="action-error"
            className="px-3 py-2 rounded-lg text-sm text-red-300 mb-4"
            style={{ background: "rgba(220,38,38,0.1)", border: "1px solid rgba(220,38,38,0.3)" }}
          >
            {actionError}
          </div>
        )}

        {/* List */}
        {loading ? (
          <div className="py-16 text-center">
            <div className="w-6 h-6 border-2 border-gold border-t-transparent rounded-full animate-spin mx-auto" />
          </div>
        ) : items.length === 0 ? (
          <div className="cbm-card p-10 text-center" data-testid="updates-empty">
            <Sparkles size={28} className="text-cbm-muted mx-auto mb-3 opacity-60" />
            <p className="text-cbm-secondary text-sm">Aucune nouveauté pour le moment.</p>
            <p className="text-cbm-muted text-xs mt-1">Revenez bientôt pour découvrir les évolutions du club.</p>
          </div>
        ) : (
          <div className="space-y-3" data-testid="updates-list">
            {items.map((u) => (
              <article
                key={u._id}
                data-testid={`update-card-${u._id}`}
                className="cbm-card p-5"
              >
                <div className="flex items-start justify-between gap-3 mb-2">
                  <h2 className="font-outfit font-semibold text-white text-base leading-snug">
                    {u.title}
                  </h2>
                  <StatusBadge status={u.status} />
                </div>
                <p className="text-cbm-secondary text-sm leading-relaxed whitespace-pre-line">
                  {u.description}
                </p>
                <div className="flex items-center justify-between gap-3 mt-3 flex-wrap">
                  <div className="text-cbm-muted text-xs">
                    {(u.status === "live" || u.status === "bugfix") && u.published_at
                      ? <>Publié le {formatDate(u.published_at)}</>
                      : null}
                  </div>
                  {isAdmin && (
                    <div className="flex items-center gap-1.5">
                      <button
                        data-testid={`edit-update-${u._id}`}
                        onClick={() => openEdit(u)}
                        className="cbm-btn-secondary text-xs py-1.5 px-2.5"
                        aria-label="Modifier"
                      >
                        <Pencil size={12} /> Modifier
                      </button>
                      <button
                        data-testid={`delete-update-${u._id}`}
                        onClick={() => { setActionError(""); setDeleteTarget(u); }}
                        className="text-xs py-1.5 px-2.5 rounded-lg font-medium transition-all inline-flex items-center gap-1.5"
                        style={{
                          background: "rgba(220,38,38,0.1)",
                          color: "#FCA5A5",
                          border: "1px solid rgba(220,38,38,0.3)",
                        }}
                        aria-label="Supprimer"
                      >
                        <Trash2 size={12} /> Supprimer
                      </button>
                    </div>
                  )}
                </div>
              </article>
            ))}
          </div>
        )}

        {/* Pagination */}
        {pages > 1 && (
          <div className="flex items-center justify-between mt-6" data-testid="updates-pagination">
            <button
              data-testid="updates-prev"
              onClick={goPrev}
              disabled={page <= 1}
              className="cbm-btn-secondary text-sm py-2 px-3 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <ChevronLeft size={14} /> Précédent
            </button>
            <span className="text-cbm-muted text-xs">
              Page {page} / {pages} · {total} nouveauté{total > 1 ? "s" : ""}
            </span>
            <button
              data-testid="updates-next"
              onClick={goNext}
              disabled={page >= pages}
              className="cbm-btn-secondary text-sm py-2 px-3 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              Suivant <ChevronRight size={14} />
            </button>
          </div>
        )}

        {/* CTA — Suggérer une idée */}
        <div className="mt-10 pt-8 border-t cbm-divider text-center">
          <p className="text-cbm-secondary text-sm mb-3">Besoin d'une amélioration ?</p>
          <button
            data-testid="suggest-idea-button"
            onClick={() => setShowSuggest(true)}
            className="cbm-btn-secondary text-sm py-2.5 px-4"
          >
            <MessageSquarePlus size={14} /> Suggérer une idée
          </button>
        </div>
      </div>

      {/* Modals */}
      <UpdateFormModal
        open={showForm}
        existing={editing}
        onClose={() => { setShowForm(false); setEditing(null); }}
        onSaved={onSaved}
      />
      <SuggestIdeaModal open={showSuggest} onClose={() => setShowSuggest(false)} />
      <ConfirmDeleteDialog
        open={Boolean(deleteTarget)}
        item={deleteTarget}
        loading={deleting}
        onCancel={() => setDeleteTarget(null)}
        onConfirm={confirmDelete}
      />
    </div>
  );
}
